// @ts-nocheck
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useClerk } from '@clerk/clerk-react';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useLocalization, type Language } from '../../../config/localization';
import LanguageSwitcher from '../../ui/navigation/LanguageSwitcher';

interface SettingsContextProps {
  selectedLanguage: Language;
  onLanguageChange: (language: Language) => void;
}

export default function SettingsContext({
  selectedLanguage,
  onLanguageChange
}: SettingsContextProps) {
  const navigate = useNavigate();
  const { signOut } = useClerk();
  const t = useLocalization(selectedLanguage);
  const { userAccess } = useOrganization();


  const userSettings = [
    {
      id: 'language',
      icon: '🌐',
      label: t.language || 'Language',
      description: t.languageDesc || 'Interface language',
      component: (
        <LanguageSwitcher
          currentLanguage={selectedLanguage}
          onLanguageChange={onLanguageChange}
        />
      )
    },
    {
      id: 'notifications',
      icon: '🔔',
      label: t.settingsContext?.notifications || 'Notifications',
      description: t.settingsContext?.notificationsDesc || 'Email and push notifications',
      component: (
        <div className="flex items-center gap-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              defaultChecked={true}
              className="mr-2 rounded border-gray-600 bg-gray-700 text-purple-600"
            />
            <span className="text-sm text-gray-300">{t.settingsContext?.enable || 'Enable'}</span>
          </label>
        </div>
      )
    }
  ];

  const systemItems = [
    {
      id: 'feedback',
      icon: '💬',
      label: t.settingsContext?.sendFeedback || 'Send Feedback',
      description: t.settingsContext?.sendFeedbackDesc || 'Report bugs or suggest features',
      action: () => window.open('mailto:support@example.com', '_blank')
    },
    {
      id: 'logout',
      icon: '🚪',
      label: t.nav?.logout || 'Logout',
      description: t.settingsContext?.logoutDesc || 'Sign out of your account',
      action: () => signOut({ redirectUrl: '/' })
    }
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-700/50">
        <h3 className="text-lg font-semibold text-white mb-1">
          ⚙️ {t.settingsContext?.title || 'Settings'}
        </h3>
        <p className="text-xs text-gray-400">
          {t.settingsContext?.subtitle || 'Customize your experience'}
        </p>
      </div>

      {/* User Settings */}
      <div className="p-4 border-b border-gray-700/50">
        <h4 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wide">
          {t.settingsContext?.userSettings || 'User Settings'}
        </h4>
        <div className="space-y-4">
          {userSettings.map((setting) => (
            <div key={setting.id} className="space-y-2">
              <div className="flex items-center gap-3">
                <span className="text-base">{setting.icon}</span>
                <div className="flex-1">
                  <div className="text-sm font-medium text-white">
                    {setting.label}
                  </div>
                  <div className="text-xs text-gray-500">
                    {setting.description}
                  </div>
                </div>
              </div>
              <div className="ml-8">
                {setting.component}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* System Settings */}
      <div className="p-4 border-b border-gray-700/50">
        <h4 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wide">
          {t.settingsContext?.system || 'System'}
        </h4>
        <div className="space-y-2">
          {systemItems.map((item) => (
            <button
              key={item.id}
              onClick={item.action}
              className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-700/50 text-gray-300 hover:text-white transition-all text-left"
            >
              <span className="text-base">{item.icon}</span>
              <div>
                <div className="text-sm font-medium">
                  {item.label}
                </div>
                <div className="text-xs text-gray-500">
                  {item.description}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* About Section */}
      <div className="flex-1 p-4">
        <h4 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wide">
          {t.settingsContext?.about || 'About'}
        </h4>
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">{t.settingsContext?.version || 'Version:'}</span>
            <span className="text-white">1.0.0</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">{t.settingsContext?.build || 'Build:'}</span>
            <span className="text-white">2024.12.01</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">{t.settingsContext?.environment || 'Environment:'}</span>
            <span className="text-white">Production</span>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-700/50">
          <p className="text-xs text-gray-500 text-center">
            {t.settingsContext?.copyright || `© ${new Date().getFullYear()} LoreMax AI`}
          </p>
          <p className="text-xs text-gray-600 text-center mt-1">
            {t.settingsContext?.madeWith || 'Made with ❤️ for creators'}
          </p>
        </div>
      </div>
    </div>
  );
}